import {
  AsyncUnzipInflate,
  AsyncZipDeflate,
  type AsyncFlateStreamHandler,
  Unzip,
  type UnzipDecoder,
  type UnzipDecoderConstructor,
  type UnzipFile,
  UnzipPassThrough,
  Zip,
} from 'fflate';

export const CHATGPT_IMPORT_MAX_BYTES = 512 * 1024 * 1024;
export const CHATGPT_IMPORT_MAX_UNCOMPRESSED_BYTES = 2 * 1024 ** 3;
export const CHATGPT_IMPORT_MAX_CONVERSATION_FILES = 200;

// The input is read only while at most this many kept files are still being
// decompressed and recompressed, so workers never hold a long queue of data
const MAX_CONCURRENT_ENTRIES = 2;

// Output chunks are folded into a Blob this often, because Chromium keeps
// large Blobs outside the JS heap
const OUTPUT_BLOB_BYTES = 16 * 1024 * 1024;

const CONVERSATIONS_FILE_NAME = /^conversations(-\d+)?\.json$/;

export class NotChatGptExportError extends Error {
  constructor() {
    super('The file is not a ChatGPT export');
  }
}

export class ChatGptExportTooLargeError extends Error {
  constructor() {
    super('The ChatGPT export is too large to import');
  }
}

const getBaseName = (path: string) => path.split('/').pop() ?? '';

const isConversationsFile = (path: string) =>
  CONVERSATIONS_FILE_NAME.test(getBaseName(path));

/**
 * Wraps a decoder so that it only decodes the files we keep.
 *
 * fflate holds on to the bytes of every entry that is never started, which
 * would put the whole export in memory. So every entry is started, and the
 * entries we skip get a decoder that drops their bytes without decompressing.
 */
function decodeKeptFilesOnly(
  Decoder: UnzipDecoderConstructor
): UnzipDecoderConstructor {
  return class KeptFileDecoder implements UnzipDecoder {
    static compression = Decoder.compression;

    ondata: AsyncFlateStreamHandler = () => undefined;
    private decoder: UnzipDecoder | null = null;

    constructor(path: string, size?: number, originalSize?: number) {
      if (!isConversationsFile(path)) return;
      this.decoder = new Decoder(path, size, originalSize);
      this.decoder.ondata = (error, chunk, final) =>
        this.ondata(error, chunk, final);
    }

    push(chunk: Uint8Array, final: boolean) {
      this.decoder?.push(chunk, final);
    }

    terminate = () => this.decoder?.terminate?.();
  };
}

/**
 * Streams a ChatGPT export zip from disk and returns a small zip holding only
 * the conversation JSON files.
 *
 * Memory stays bounded, because the input is read in chunks, each kept file is
 * decompressed and recompressed in workers as it streams past, and every other
 * file is skipped without being decompressed.
 */
export function extractChatGptExport(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = file.stream().getReader();
    const outputBlobs: Blob[] = [];
    let pendingChunks: Uint8Array[] = [];
    let pendingBytes = 0;
    const keptFiles: UnzipFile[] = [];
    const keptFilesFinished: Promise<void>[] = [];
    let outputBytes = 0;
    let uncompressedBytes = 0;
    let hasFailed = false;
    let entriesInFlight = 0;
    let wakeReader: (() => void) | undefined;

    const zip = new Zip((error, chunk, final) => {
      if (error) return fail(error);

      pendingChunks.push(chunk);
      pendingBytes += chunk.length;
      outputBytes += chunk.length;
      if (outputBytes > CHATGPT_IMPORT_MAX_BYTES) {
        return fail(new ChatGptExportTooLargeError());
      }
      if (pendingBytes >= OUTPUT_BLOB_BYTES || final) {
        // fflate types its chunks as ArrayBufferLike, but they are never shared
        outputBlobs.push(new Blob(pendingChunks as BlobPart[]));
        pendingChunks = [];
        pendingBytes = 0;
      }
      if (final) {
        resolve(
          new File(outputBlobs, 'chatgpt-export.zip', {
            type: 'application/zip',
          })
        );
      }
    });

    const unzip = new Unzip((entry) => {
      if (!isConversationsFile(entry.name)) {
        entry.ondata = () => undefined;
        entry.start();
        return;
      }

      // Every kept file gets its own decompression and compression workers
      if (keptFiles.length >= CHATGPT_IMPORT_MAX_CONVERSATION_FILES) {
        return fail(new ChatGptExportTooLargeError());
      }
      keptFiles.push(entry);

      const zipEntry = new AsyncZipDeflate(entry.name);
      zip.add(zipEntry);

      // `zip.add` sets `ondata`, so wrap it to learn when this entry is done
      entriesInFlight++;
      const emitToZip = zipEntry.ondata;
      zipEntry.ondata = (error, chunk, final) => {
        emitToZip(error, chunk, final);
        if (final) {
          entriesInFlight--;
          wakeReader?.();
        }
      };

      keptFilesFinished.push(
        new Promise((entryFinished) => {
          entry.ondata = (error, chunk, final) => {
            if (error) return fail(error);
            uncompressedBytes += chunk.length;
            if (uncompressedBytes > CHATGPT_IMPORT_MAX_UNCOMPRESSED_BYTES) {
              return fail(new ChatGptExportTooLargeError());
            }
            // Pass a copy: the deflate worker takes ownership of the buffer, and
            // a stored entry's chunk is a view into the unzip's live input
            zipEntry.push(new Uint8Array(chunk), final);
            if (final) entryFinished();
          };
        })
      );
      entry.start();
    });
    unzip.register(decodeKeptFilesOnly(AsyncUnzipInflate));
    unzip.register(decodeKeptFilesOnly(UnzipPassThrough));

    function fail(error: Error) {
      if (hasFailed) return;
      hasFailed = true;
      zip.terminate();
      keptFiles.forEach((keptFile) => keptFile.terminate());
      void reader.cancel();
      wakeReader?.();
      reject(error);
    }

    async function readExport() {
      for (;;) {
        // Earlier entries have all their input pushed, so one of them always finishes
        while (entriesInFlight >= MAX_CONCURRENT_ENTRIES) {
          await new Promise<void>((resolve) => (wakeReader = resolve));
          if (hasFailed) return;
        }
        const { done, value } = await reader.read();
        if (hasFailed) return;
        if (done) break;
        unzip.push(value);
      }
      unzip.push(new Uint8Array(0), true);

      await Promise.all(keptFilesFinished);
      if (hasFailed) return;
      if (keptFiles.length === 0) return fail(new NotChatGptExportError());
      zip.end();
    }

    readExport().catch(fail);
  });
}
